import React, { useEffect, useState } from 'react';
import { Star, Play, Eye, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * AdminSpotlightPanel - admin control surface for the Weekly Mover Spotlight.
 *
 * Three actions:
 *   - Preview: dry-run, returns Claude draft + mover stats, NO send
 *   - Run: manual broadcast (uses the same code path the Monday scheduler hits)
 *   - Status: last 5 historical runs with deliverability metadata + canary state
 */
const AdminSpotlightPanel = () => {
  const [runs, setRuns] = useState(null);
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [error, setError] = useState(null);
  const [lastRunMsg, setLastRunMsg] = useState(null);

  const loadStatus = async () => {
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/mover-spotlight/status`, {
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setRuns(d.runs || []);
    } catch (e) {
      setError(String(e.message || e));
      setRuns([]);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const doPreview = async () => {
    setLoadingPreview(true);
    setError(null);
    setPreview(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/mover-spotlight/preview`, {
        credentials: 'include',
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${r.status}`);
      }
      setPreview(await r.json());
    } catch (e) {
      setError(`Preview failed: ${e.message || e}`);
    } finally {
      setLoadingPreview(false);
    }
  };

  const doRun = async () => {
    if (!window.confirm(
      "Send this week's Mover Spotlight to every active newsletter subscriber NOW? " +
      "This will also DM the spotlighted member. There is no undo."
    )) return;
    setRunning(true);
    setError(null);
    setLastRunMsg(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/mover-spotlight/run`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      if (!data.ran) {
        setLastRunMsg({
          tone: 'warn',
          text: `Skipped - reason: ${data.reason || 'unknown'}`,
        });
      } else {
        setLastRunMsg({
          tone: 'success',
          text: `Dispatched "${data.subject}" - ${data.sent}/${data.total_subscribers} delivered (${data.failed} failed). ` +
                `Member DM: ${data.member_notified?.email_sent ? 'sent' : 'skipped'}.`,
        });
      }
      await loadStatus();
    } catch (e) {
      setError(`Run failed: ${e.message || e}`);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      className="bg-[#FFFFFF] border border-[#F4ECD8] p-6"
      data-testid="admin-spotlight-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="inline-flex items-center gap-2 mb-1">
            <Star className="w-4 h-4 text-[#E8A830]" />
            <h3 className="font-semibold text-[#0A1F2E]">Mover Spotlight</h3>
            <span className="ml-1 font-mono uppercase text-[9px] tracking-widest text-[#1A5C6E] bg-[#F4ECD8] px-2 py-0.5 rounded-full">
              Mon · 09:00 UTC
            </span>
          </div>
          <p className="text-xs text-[#5A6B73] max-w-md">
            Auto-picks the top weekly mover, Claude drafts the profile, broadcasts to active subscribers,
            and DMs the spotlighted member with a one-click LinkedIn share.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={doPreview}
            disabled={loadingPreview || running}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-mono uppercase tracking-wider font-semibold border border-[#0A1F2E]/20 rounded-full hover:bg-[#0A1F2E] hover:text-[#F9F6EF] transition disabled:opacity-50"
            data-testid="admin-spotlight-preview"
          >
            <Eye className="w-3.5 h-3.5" />
            {loadingPreview ? 'Drafting…' : 'Preview'}
          </button>
          <button
            type="button"
            onClick={doRun}
            disabled={running || loadingPreview}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-mono uppercase tracking-wider font-semibold bg-[#E8A830] text-[#0A1F2E] rounded-full hover:brightness-95 transition disabled:opacity-50"
            data-testid="admin-spotlight-run"
          >
            <Play className="w-3.5 h-3.5" />
            {running ? 'Sending…' : 'Run now'}
          </button>
          <button
            type="button"
            onClick={loadStatus}
            className="p-2 text-[#0A1F2E]/60 hover:text-[#0A1F2E]"
            title="Refresh status"
            data-testid="admin-spotlight-refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
          {error}
        </div>
      )}

      {lastRunMsg && (
        <div
          className={`mb-3 p-3 border text-sm rounded inline-flex items-start gap-2 ${
            lastRunMsg.tone === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-amber-50 border-amber-200 text-amber-800'
          }`}
          data-testid="admin-spotlight-last-run"
        >
          <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{lastRunMsg.text}</span>
        </div>
      )}

      {preview && (
        <div className="mb-5 border border-[#F4ECD8] bg-[#F9F6EF] p-4 rounded"
             data-testid="admin-spotlight-preview-card">
          <p className="font-mono uppercase text-[9px] tracking-widest text-[#1A5C6E] mb-2">
            Preview (dry-run) - no emails sent
          </p>
          {preview.mover && (
            <p className="font-display italic text-2xl text-[#0A1F2E] mb-1"
               data-testid="admin-spotlight-preview-headline">
              {preview.draft?.headline || preview.preview_subject}
            </p>
          )}
          <p className="text-xs text-[#5A6B73] mb-3 font-mono">
            {preview.mover
              ? `${preview.mover.first_name} ${preview.mover.last_name_initial || ''} · ${preview.mover.destination_city || 'unspecified city'} · +${preview.mover.points_gained} pts this week · rank ${preview.mover.current_rank}`
              : `No mover this week - ${preview.reason || 'skipped'}`}
          </p>
          {preview.draft?.dek && (
            <p className="font-display italic text-[#0A1F2E]/80 text-base mb-2">
              {preview.draft.dek}
            </p>
          )}
          {preview.draft?.body_markdown && (
            <p className="text-sm text-[#0A1F2E]/75 whitespace-pre-line line-clamp-6">
              {preview.draft.body_markdown}
            </p>
          )}
        </div>
      )}

      <div className="border-t border-[#F4ECD8] pt-4">
        <p className="font-mono uppercase text-[10px] tracking-widest text-[#5A6B73] mb-3">
          Last 5 sends
        </p>
        {runs === null ? (
          <div className="space-y-2">
            {[0, 1, 2].map(i => (
              <div key={i} className="h-12 bg-[#F4ECD8] animate-pulse rounded" />
            ))}
          </div>
        ) : runs.length === 0 ? (
          <p className="text-sm text-[#5A6B73]">No spotlight runs yet.</p>
        ) : (
          <ul className="space-y-2" data-testid="admin-spotlight-runs">
            {runs.map((r) => {
              const ratio = r.total_subscribers ? (r.failed || 0) / r.total_subscribers : 0;
              const canaryTripped = r.canary?.tripped;
              return (
                <li
                  key={r.id || r.ran_at}
                  className="flex items-start justify-between gap-3 p-3 bg-[#F9F6EF]/50 rounded text-sm"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-display italic text-[#0A1F2E] truncate">
                      {r.subject || (r.ran ? 'Sent' : `Skipped (${r.reason || 'unknown'})`)}
                    </div>
                    <div className="text-xs text-[#5A6B73] font-mono mt-0.5">
                      {new Date(r.ran_at).toLocaleString('en-US', { timeZone: 'America/Chicago' })}
                      {r.mover_first_name ? ` · ${r.mover_first_name} · +${r.points_gained || 0} pts` : ''}
                    </div>
                  </div>
                  <div className="text-right text-xs font-mono whitespace-nowrap">
                    {r.ran ? (
                      <>
                        <div className={`${canaryTripped ? 'text-red-600 font-semibold' : 'text-emerald-700'}`}>
                          {r.sent ?? 0}/{r.total_subscribers ?? 0} sent
                        </div>
                        <div className="text-[#5A6B73] mt-0.5">
                          {(r.failed ?? 0)} failed · {(ratio * 100).toFixed(0)}%
                        </div>
                        {canaryTripped && (
                          <div
                            className="inline-flex items-center gap-1 text-red-600 mt-1"
                            data-testid="admin-spotlight-canary-tripped"
                          >
                            <AlertTriangle className="w-3 h-3" />
                            CANARY
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="text-amber-700">Skipped</span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminSpotlightPanel;
